import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import PageHero from '../components/common/PageHero';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, QrCode, Download, RefreshCw, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const MyEvents = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);

  useEffect(() => {
    fetchMyRegistrations();
  }, []);

  const fetchMyRegistrations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backendUrl}/api/events/my-registrations`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setRegistrations(response.data || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your events',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchQRCode = async (eventId, registrationId) => {
    try {
      setLoadingQR(true);
      const response = await axios.get(
        `${backendUrl}/api/events/registrations/${registrationId}/qr`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setQrCode(response.data);
    } catch (error) {
      console.error('Error fetching QR code:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate QR code',
        variant: 'destructive'
      });
    } finally {
      setLoadingQR(false);
    }
  };

  const handleViewQRCode = (registration) => {
    setSelectedEvent(registration);
    fetchQRCode(registration.event_id, registration.id);
  };

  const handleDownloadQR = () => {
    if (qrCode && qrCode.qr_code_image_url) {
      const dataUrl = qrCode.qr_code_image_url;
      const link = document.createElement('a');
      // Convert base64 data URI to blob for proper download
      const byteString = atob(dataUrl.split(',')[1]);
      const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      link.href = URL.createObjectURL(blob);
      link.download = `qr-code-${selectedEvent.event_title || 'event'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  const getEventStatus = (registration) => {
    const eventDate = new Date(registration.event_date);
    const today = new Date();
    
    if (registration.attendance_status === 'attended') {
      return { label: 'Attended', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    }
    
    if (eventDate < today) {
      return { label: 'Past Event', color: 'bg-gray-100 text-gray-800', icon: Clock };
    }
    
    return { label: 'Upcoming', color: 'bg-blue-100 text-blue-800', icon: Calendar };
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <PageHero title="My Events" />
        <div className="container mx-auto px-4 py-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="My Events" />
      
      <div className="container mx-auto px-4 py-8 md:py-12">
        {registrations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Events Yet</h3>
              <p className="text-gray-600 mb-4">You haven&apos;t registered for any events.</p>
              <Button onClick={() => window.location.href = '/events'}>Browse Events</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {registrations.map((registration) => {
              const status = getEventStatus(registration);
              const StatusIcon = status.icon;
              
              return (
                <Card key={registration.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{registration.event_title}</CardTitle>
                      <span className={`text-xs px-2 py-1 rounded-full ${status.color} flex items-center gap-1`}>
                        <StatusIcon size={12} />
                        {status.label}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="mr-2 h-4 w-4" />
                      <span>{new Date(registration.event_date).toLocaleDateString('en-ZA', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    
                    {registration.event_time && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="mr-2 h-4 w-4" />
                        <span>{registration.event_time}</span>
                      </div>
                    )}
                    
                    {registration.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="mr-2 h-4 w-4" />
                        <span className="line-clamp-2">{registration.location}</span>
                      </div>
                    )}
                    
                    <div className="pt-4 border-t space-y-2">
                      <Button 
                        onClick={() => window.location.href = `/event-checkin/${registration.event_id}`}
                        className="w-full bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Check In/Out
                      </Button>
                      <Button 
                        onClick={() => handleViewQRCode(registration)}
                        variant="outline"
                        className="w-full"
                        size="sm"
                      >
                        <QrCode className="mr-2 h-4 w-4" />
                        View QR Code
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {selectedEvent && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedEvent(null)}
        >
          <Card 
            className="w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader>
              <CardTitle className="text-center">{selectedEvent.event_title}</CardTitle>
              <p className="text-sm text-center text-gray-600">Your Check-In QR Code</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingQR ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
                </div>
              ) : qrCode ? (
                <>
                  <div className="bg-white p-6 rounded-lg border-2 border-gray-200 flex justify-center">
                    <img 
                      src={qrCode.qr_code_image_url} 
                      alt="QR Code" 
                      className="w-64 h-64 object-contain"
                    />
                  </div>
                  
                  <div className="text-center space-y-2">
                    <p className="text-sm text-gray-600">
                      Show this QR code at the event entrance for check-in
                    </p>
                    {qrCode.expires_at && (
                      <p className="text-xs text-gray-500">
                        Valid until: {new Date(qrCode.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleDownloadQR}
                      variant="outline"
                      className="flex-1"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                    <Button 
                      onClick={() => fetchQRCode(selectedEvent.event_id, selectedEvent.id)}
                      variant="outline"
                      className="flex-1"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Refresh
                    </Button>
                  </div>
                  
                  <Button 
                    onClick={() => setSelectedEvent(null)}
                    variant="secondary"
                    className="w-full"
                  >
                    Close
                  </Button>
                </>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-600">Failed to load QR code</p>
                  <Button 
                    onClick={() => fetchQRCode(selectedEvent.event_id, selectedEvent.id)}
                    className="mt-4"
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MyEvents;